import { useEffect, useState } from 'react';
import { PROVIDER_MODELS, ACTION_TYPE_LABELS } from '../../constants';

const TASK_TYPES = [
  'chat_reply',
  'context_select',
  'memoize',
  'extract_insight',
  'tag_extract_insight',
  'audit',
  'default'
];

const TASK_LABELS = {
  ...ACTION_TYPE_LABELS,
  context_select: 'コンテキスト選択',
  audit: '監査・分析',
  default: 'デフォルト（共通）'
};

export default function AdminModelPanel({ user }) {
  const [routes, setRoutes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    let isMounted = true;
    async function performFetch() {
      if (!user) {
        if (isMounted) {
          setRoutes({});
          setLoading(false);
        }
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const resp = await fetch('/api/v1/admin/config/model-routing', {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (!resp.ok) throw new Error(`Failed to fetch config: ${resp.status}`);
        const data = await resp.json();
        if (isMounted) {
          setRoutes(data.routes || {});
          setError(null);
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    performFetch();
    return () => { isMounted = false; };
  }, [user, refreshTrigger]);

  const handleUpdateRoute = (task, field, value) => {
    setRoutes(prev => {
      const current = prev[task] || { provider: 'gemini', model: 'gemini-3.1-flash-lite' };
      const updated = { ...current, [field]: value };

      // If provider changes, ensure model is valid
      if (field === 'provider' && PROVIDER_MODELS[value]) {
        updated.model = PROVIDER_MODELS[value][0];
      }

      return { ...prev, [task]: updated };
    });
  };

  const handleRefresh = () => {
    setLoading(true);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSave = async () => {
    if (isProcessing || !user) return;
    setIsProcessing(true);
    setSuccessMessage('');
    try {
      const idToken = await user.getIdToken();
      const resp = await fetch('/api/v1/admin/config/model-routing', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ routes })
      });

      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error?.message || 'Save failed');
      }

      setSuccessMessage('ルーティングを更新しました（反映まで最大5分かかります）');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return <div className="p-4 text-gray-400">Loading config...</div>;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-900 overflow-hidden">
      <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">Model Routing Config</h3>
        <div className="flex items-center gap-4">
          {successMessage && <span className="text-xs text-emerald-400 font-bold">{successMessage}</span>}
          <button onClick={handleRefresh} disabled={loading} className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-rose-900/20 text-rose-400 text-xs border-b border-rose-900/30">Error: {error}</div>}

      <div className="flex-1 overflow-y-auto p-4">
        <div className="bg-gray-800/40 border border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-gray-800 text-gray-400">
              <tr>
                <th className="p-3 border-b border-gray-700">タスク</th>
                <th className="p-3 border-b border-gray-700">プロバイダー</th>
                <th className="p-3 border-b border-gray-700">モデル</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {TASK_TYPES.map(task => {
                const config = routes[task] || { provider: 'gemini', model: 'gemini-3.1-flash-lite' };
                return (
                  <tr key={task} className="hover:bg-gray-800/40 transition-colors">
                    <td className="p-3">
                      <div className="font-medium text-white">{TASK_LABELS[task] || task}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{task}</div>
                    </td>
                    <td className="p-3">
                      <select
                        value={config.provider}
                        onChange={(e) => handleUpdateRoute(task, 'provider', e.target.value)}
                        className="bg-gray-900 border border-gray-700 text-gray-300 rounded px-2 py-1 outline-none focus:border-blue-500"
                      >
                        <option value="gemini">Gemini</option>
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <select
                        value={config.model}
                        onChange={(e) => handleUpdateRoute(task, 'model', e.target.value)}
                        className="w-full bg-gray-900 border border-gray-700 text-gray-300 rounded px-2 py-1 outline-none focus:border-blue-500"
                      >
                        {(PROVIDER_MODELS[config.provider] || []).map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p-4 border-t border-gray-800 bg-gray-900/50">
        <button
          onClick={handleSave}
          disabled={isProcessing}
          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 rounded-lg transition-all disabled:opacity-50"
        >
          {isProcessing ? '保存中...' : '設定を保存'}
        </button>
      </div>
    </div>
  );
}
